/**
 * Company lookup — now a CONFIGURATION of the generic @solum/ui <Lookup> (dialog
 * presentation), not a bespoke dialog. It supplies the registry search source,
 * how a hit renders, and the "attach registered address" footer; the primitive
 * owns the input/debounce/keyboard/presentation. Applies a hit to the account:
 * legal name, VAT, website, optionally the registered address onto the graph.
 */
import { useRef, useState } from 'react'
import { Choice, Lookup, type LookupItem, type LookupSection } from '@solum/ui'
import { searchCompany, useCompanySettings, type CompanyHit } from './companyLookup'

export function CompanyLookupDialog({ open, onClose, onApply }: {
  open: boolean
  onClose: () => void
  onApply: (hit: CompanyHit, attachAddress: boolean) => void | Promise<void>
}) {
  const settings = useCompanySettings()
  const [attach, setAttach] = useState(true)
  const byId = useRef(new Map<string, CompanyHit>())
  if (!open) return null

  const enabled = Object.entries(settings.registries).filter(([, v]) => v).map(([k]) => k)

  const search = async (q: string): Promise<LookupSection[]> => {
    const hits = await searchCompany(q, settings)
    byId.current.clear()
    const items: LookupItem[] = hits.map((h, i) => {
      const id = `${h.source}:${h.name}:${h.number ?? h.vat ?? i}`
      byId.current.set(id, h)
      return {
        id,
        primary: h.name,
        secondary: [h.vat ?? h.number, [h.street, h.postal, h.city].filter(Boolean).join(', ')].filter(Boolean).join(' · '),
        badge: { label: `${h.source} · ${h.country}`, tone: h.source === 'builtin' ? 'neutral' : 'brand' },
      }
    })
    return [{ items }]
  }

  return (
    <Lookup
      presentation="dialog"
      testid="company-lookup"
      title="Look up company"
      hint={`registries: ${enabled.join(' · ') || 'none — enable one in Settings'}`}
      placeholder="Company name…"
      minChars={3}
      debounceMs={250}
      search={search}
      emptyText="Type at least 3 characters — results come from the enabled registries."
      onClose={onClose}
      /* The registry apply writes an entity patch and, optionally, an address
         plus an edge. Returned rather than `void`ed, so the dialog holds until
         all of it has landed and a refusal leaves it open (#2149). */
      onPick={(it) => { const hit = byId.current.get(it.id); return hit ? onApply(hit, attach) : undefined }}
      footer={
        /* The kit's described choice (#2406 cards-inputs), not a bare browser
           checkbox in an inline-styled label with a raw 8px gap: the option's
           words name it and the consequence is its description. */
        <Choice
          label="Attach the registered address to this account"
          description="It lands on the graph and the map."
          checked={attach}
          onChange={(e) => setAttach(e.target.checked)}
        />
      }
    />
  )
}
